import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { FeatureFlag, FeatureFlagId } from '../../../../@types/featureFlags';

import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslator } from 'translation/useTranslator';
import { FeatureFlagOrganizationDto } from '../../../../@types/organization';
import { SelectItem } from 'components/util/select/selectData';
import { AccessScopeInput } from './AccessScopeInput';
import { Button, TextField } from '@nps.today/component-library';
import CustomSwitch from 'components/util/CustomSwitch';

interface EditFeatureFlagDialogProps {
  open: boolean;
  organizations: FeatureFlagOrganizationDto[];
  selectedFeatureFlag: FeatureFlag;
  onClose: () => void;
  onSave: (flag: FeatureFlag) => Promise<void>;
}
export const EditFeatureFlagDialog: FunctionComponent<EditFeatureFlagDialogProps> = ({
  open,
  organizations,
  selectedFeatureFlag,
  onClose,
  onSave
}) => {
  const translator = useTranslator();
  const [featureFlag, setFeatureFlag] = useState<FeatureFlag>(selectedFeatureFlag);
  const [loading, setLoading] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState<SelectItem[]>([]);

  const handleSave = async () => {
    setLoading(true);
    await onSave({
      ...featureFlag,
      organizations: selectedOrganizations.map((o) => o.value)
    });
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      setFeatureFlag(selectedFeatureFlag);
    }
  }, [open, selectedFeatureFlag]);

  useEffect(() => {
    setSelectedOrganizations(
      organizations
        .filter((org) => featureFlag.organizations?.includes(org.id))
        .map((org) => ({ value: org.id, displayValue: org.name }))
    );
  }, [organizations, featureFlag.organizations]);

  const handleClose = () => {
    setFeatureFlag(selectedFeatureFlag);
    setSelectedOrganizations(
      organizations
        .filter((org) => selectedFeatureFlag.organizations?.includes(org.id))
        .map((org) => ({ value: org.id, displayValue: org.name }))
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>{translator.editFeatureFlag()}</DialogTitle>
      <DialogContent>
        <Stack gap={3} sx={{ pt: 1 }}>
          <TextField
            dataTestId="edit-feature-flag-dialog-name"
            disabled={true}
            value={FeatureFlagId[featureFlag.id]}
            onChange={(event) => event.stopPropagation()}
            label={translator.name()}
            color={'primary'}
            required
            helperText={translator.featureFlagNamesCannotBeChangedAfterCreation()}
          />
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.neutral'
            }}
          >
            <Stack gap={2}>
              <CustomSwitch
                fullWidth
                labelPlacement="start"
                label={translator.enabled()}
                helperText={
                  featureFlag.enabled
                    ? translator.featureFlagEnabledDescription()
                    : translator.featureFlagDisabledDescription()
                }
                switchProps={{
                  disabled: loading,
                  checked: featureFlag.enabled,
                  onChange: (_, checked) => setFeatureFlag({ ...featureFlag, enabled: checked })
                }}
              />
              <CustomSwitch
                fullWidth
                labelPlacement="start"
                label={translator.enabledForEveryone()}
                helperText={translator.featureFlagEnabledForEveryoneDescription()}
                switchProps={{
                  disabled: loading || !featureFlag.enabled,
                  checked: featureFlag.enabledForEveryone,
                  onChange: (_, checked) =>
                    setFeatureFlag({ ...featureFlag, enabledForEveryone: checked })
                }}
              />
            </Stack>
          </Box>
          <TextField
            dataTestId="edit-feature-flag-dialog-description"
            multiline
            disabled={loading}
            rows={3}
            maxRows={10}
            label={translator.description()}
            fullWidth
            value={featureFlag.description}
            onChange={(e) => setFeatureFlag({ ...featureFlag, description: e.target.value })}
          />
          <AccessScopeInput
            dataTestId="edit-feature-flag-dialog-organizations"
            options={organizations.map((o) => ({
              value: o.id,
              displayValue: o.name
            }))}
            value={selectedOrganizations}
            setValue={setSelectedOrganizations}
            label={translator.organizationAccess()}
            helperText={
              featureFlag.enabledForEveryone
                ? translator.organizationAccessIgnoredWhenEnabledForEveryone()
                : translator.enabledForTheListedOrganizations()
            }
            loading={loading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          dataTestId="edit-feature-flag-dialog-cancel"
          onClick={handleClose}
          disabled={loading}
        >
          {translator.cancel()}
        </Button>
        <Button
          dataTestId="edit-feature-flag-dialog-save"
          loading={loading}
          onClick={handleSave}
          autoFocus
          variant="contained"
        >
          {translator.save()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
